import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import UpMasterSidebar from './components/UpMasterSidebar';
import DataSourceRequestModal from './components/DataSourceRequestModal';
import VideoList from './components/VideoList';
import ChatPanel from './components/ChatPanel';
import DocumentPanel from './components/DocumentPanel';
import CalendarWidget from './components/CalendarWidget';
import PlanView from './components/PlanView';
import Sidebar from './components/Sidebar';
import CoachView from './components/CoachView';
import ConversationSidebar from './components/ConversationSidebar';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import api, { proxyImage, Conversation } from './services/api';
import './App.css';

// ── Error Boundary ──
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 48, textAlign: 'center', color: '#f87171' }}>
          <h2>⚠ 页面发生错误</h2>
          <p style={{ color: '#a3a3a3', marginTop: 8 }}>
            {this.state.error?.message || '未知错误'}
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ marginTop: 16, padding: '8px 24px', borderRadius: 8, border: 'none', background: '#18e299', color: '#052e16', cursor: 'pointer' }}
          >
            刷新页面
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface UpMaster {
  id: number;
  uid: string;
  name: string;
  face: string;
  video_count: number;
  created_at: string;
}

function AppContent() {
  const { user, loading } = useAuth();
  const [activeNav, setActiveNav] = useState('coaches');  // v21：登录默认进入教练页
  const [health, setHealth] = useState<{ llm_connected: boolean } | null>(null);

  // UpMaster state (shared)
  const [selectedUpMaster, setSelectedUpMaster] = useState<UpMaster | null>(null);
  const [activeUpMasterTab, setActiveUpMasterTab] = useState<'videos' | 'chat'>('videos');
  // 申请数据源弹窗
  const [requestOpen, setRequestOpen] = useState(false);
  // 当前 up 主的订阅状态（知识库信息卡订阅按钮）
  const [subscribedIds, setSubscribedIds] = useState<Set<number>>(new Set());
  const [subError, setSubError] = useState('');
  const [skillMsg, setSkillMsg] = useState('');
  const [skillMenuOpen, setSkillMenuOpen] = useState(false);
  const skillMenuRef = useRef<HTMLDivElement>(null);

  // 加载我的订阅（知识库信息卡按钮用）
  useEffect(() => {
    let cancelled = false;
    api.getMyDataSources()
      .then(res => {
        if (!cancelled) setSubscribedIds(new Set((res.data?.data || []).map((s: any) => s.id)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Skill 下拉菜单：点击外部关闭（参考 UpMasterSwitcher 的 document mousedown 模式）
  useEffect(() => {
    if (!skillMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (skillMenuRef.current && !skillMenuRef.current.contains(e.target as Node)) setSkillMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [skillMenuOpen]);

  // Conversation state
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);

  // SWR: upmasters（缓存优先，后台刷新替换）
  const { data: upmastersData, mutate: mutateUpmasters } = useSWR<UpMaster[]>(
    user ? ['upmasters'] : null,
    () => api.getUpMasters().then(r => r.data.data || [])
  );
  const upmasters = upmastersData || [];

  // SWR: conversations（缓存优先，后台刷新替换）
  const { data: conversationsData, mutate: mutateConversations } = useSWR<Conversation[]>(
    user ? ['conversations'] : null,
    () => api.getConversations().then(r => r.data.data || [])
  );
  const conversations = conversationsData || [];

  const checkHealth = async () => {
    try { const res = await api.healthCheck(); setHealth(res.data); } catch (e) {}
  };

  // 首次拿到 upmasters 后默认选中第一个
  useEffect(() => {
    if (upmasters.length > 0 && !selectedUpMaster) setSelectedUpMaster(upmasters[0]);
  }, [upmasters, selectedUpMaster]);

  useEffect(() => {
    if (user) checkHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return <div className="app-loading">加载中...</div>;
  if (!user) return <LoginPage />;

  const handleSelectUpMaster = (um: UpMaster) => {
    setSelectedUpMaster(um);
    setActiveUpMasterTab('videos');
  };

  const handleToggleSubscribe = async (um: UpMaster) => {
    setSubError('');
    try {
      const next = new Set(subscribedIds);
      if (next.has(um.id)) next.delete(um.id);
      else next.add(um.id);
      setSubscribedIds(next);
      const res = await api.updateMyDataSources(Array.from(next));
      setSubscribedIds(new Set((res.data?.data || []).map((s: any) => s.id)));
    } catch (err: any) {
      setSubError(err.response?.data?.detail || '订阅操作失败');
    }
  };

  const handleDownloadSkill = async (um: UpMaster) => {
    setSkillMenuOpen(false);
    setSkillMsg('');
    try {
      const res = await api.downloadSkill(um.id);
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${um.name}-skill.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const status = err?.response?.status;
      setSkillMsg((status === 404 || status === 409)
        ? '该 up 主暂无 skill 或正在生成中'
        : '下载失败，请稍后重试');
    }
  };

  const handleCopySkillInstall = async (um: UpMaster) => {
    setSkillMenuOpen(false);
    setSkillMsg('');
    try {
      const res = await api.getSkillInstallText(um.id);
      await navigator.clipboard.writeText(res.data);
      setSkillMsg('已复制，粘贴给 agent 即可安装');
    } catch (err: any) {
      const status = err?.response?.status;
      setSkillMsg((status === 404 || status === 409)
        ? '该 up 主暂无 skill 或正在生成中'
        : '复制失败，请稍后重试');
    }
  };

  const handleNavigate = (id: string) => {
    setActiveNav(id);
  };

  const handleNewConversation = () => {
    setActiveConversationId(null);
  };

  const handleSelectConversation = (id: number | null) => {
    setActiveConversationId(id);
  };

  const handleConversationCreated = (id: number) => {
    setActiveConversationId(id);
    mutateConversations();
  };

  const handleDeleteConversation = async (id: number) => {
    try {
      await api.deleteConversation(id);
      mutateConversations();
      if (activeConversationId === id) setActiveConversationId(null);
    } catch (e) {
      console.error('删除会话失败:', e);
    }
  };

  const handleTogglePin = async (id: number) => {
    try {
      const conv = conversations.find(c => c.id === id);
      if (!conv) return;
      if (conv.pinned) {
        await api.unpinConversation(id);
      } else {
        await api.pinConversation(id);
      }
      mutateConversations();
    } catch (e) {
      console.error('置顶会话失败:', e);
    }
  };

  const renderContent = () => {
    switch (activeNav) {
      case 'coaches':
        return <CoachView />;
      case 'chat':
        return (
          <div className="chat-layout flex h-full min-h-0">
            <ConversationSidebar
              conversations={conversations}
              activeId={activeConversationId}
              onSelect={handleSelectConversation}
              onCreate={handleNewConversation}
              onDelete={handleDeleteConversation}
              onTogglePin={handleTogglePin}
            />
            <ChatPanel
              upmasters={upmasters}
              conversationId={activeConversationId}
              onConversationCreated={handleConversationCreated}
              onConversationUpdated={() => mutateConversations()}
            />
          </div>
        );
      case 'calendar':
        return (
          <div className="page-scroll">
            <div className="page-view">
              <div className="page-header">
                <h2>📅 训练日历</h2>
                <p className="page-subtitle">查看训练历史，点击日期查看详情</p>
              </div>
              <CalendarWidget />
            </div>
          </div>
        );
      case 'upmasters': {
        // 渲染兜底：进入知识库页直接有选中的 up 主（不依赖 useEffect 时序）
        const activeUm = selectedUpMaster ?? upmasters[0] ?? null;
        return (
          <div className="kb-layout flex h-full min-h-0">
            <UpMasterSidebar
              upmasters={upmasters}
              selectedId={selectedUpMaster?.id}
              onSelect={handleSelectUpMaster}
              onRequest={() => setRequestOpen(true)}
              isAdmin={user?.role === 'admin'}
              onAdded={() => mutateUpmasters()}
            />
            <div className={`kb-main flex-1 min-w-0 ${activeUpMasterTab === 'chat' ? 'kb-main-chat' : 'overflow-y-auto'}`}>
              <div className="page-view">
                {upmasters.length === 0 ? (
                  <div className="card empty-state">
                    <div className="empty-state-icon">📚</div>
                    <h3 className="empty-state-title">暂无 Up 主</h3>
                    <p className="empty-state-desc">点击左下角「＋ 添加数据源」提交链接即可自动同步</p>
                  </div>
                ) : activeUm ? (
                  <>
                    <div className="page-header">
                      <h2>📚 知识库</h2>
                      <p className="page-subtitle">浏览 Up 主视频，订阅数据源</p>
                    </div>

                    <div className="card">
                      <div className="upmaster-header">
                        <img loading="lazy" src={proxyImage(activeUm.face, 80, 80)} alt={activeUm.name} className="upmaster-avatar-sm" />
                        <div className="upmaster-header-info">
                          <h2 className="upmaster-name">{activeUm.name}</h2>
                          <p className="upmaster-followers">{activeUm.video_count} 个视频</p>
                        </div>
                        <div className="upmaster-header-actions">
                          <button
                            className={`btn btn-sm ${subscribedIds.has(activeUm.id) ? 'btn-subscribed' : 'btn-brand'}`}
                            onClick={() => handleToggleSubscribe(activeUm)}
                          >
                            {subscribedIds.has(activeUm.id) ? '已订阅' : '订阅'}
                          </button>
                          <div className="upmaster-skill-menu" ref={skillMenuRef}>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => setSkillMenuOpen(o => !o)}
                              aria-haspopup="menu"
                              aria-expanded={skillMenuOpen}
                            >
                              📥 Skill <span className="upmaster-skill-arrow">▾</span>
                            </button>
                            {skillMenuOpen && (
                              <div className="upmaster-skill-dropdown" role="menu">
                                <button
                                  className="upmaster-skill-item"
                                  role="menuitem"
                                  onClick={() => handleCopySkillInstall(activeUm)}
                                >
                                  📋 复制安装指令
                                </button>
                                <button
                                  className="upmaster-skill-item"
                                  role="menuitem"
                                  onClick={() => handleDownloadSkill(activeUm)}
                                >
                                  ⬇️ 下载 .md
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {skillMsg && <p className="skill-msg" style={{ marginTop: 8 }}>{skillMsg}</p>}
                      {subError && <p className="error-text" style={{ marginTop: 8 }}>{subError}</p>}
                    </div>

                    <div className="tabs">
                      <button className={`tab ${activeUpMasterTab === 'videos' ? 'active' : ''}`} onClick={() => setActiveUpMasterTab('videos')}>📹 视频</button>
                      <button className={`tab ${activeUpMasterTab === 'chat' ? 'active' : ''}`} onClick={() => setActiveUpMasterTab('chat')}>💬 问答</button>
                    </div>
                    {activeUpMasterTab === 'videos' ? (
                      <VideoList key={activeUm.id} upmasterId={activeUm.id} />
                    ) : (
                      <div className="kb-chat flex-1 min-h-0">
                        <ChatPanel
                          upmasters={upmasters}
                          conversationId={activeConversationId}
                          onConversationCreated={handleConversationCreated}
                          onConversationUpdated={() => mutateConversations()}
                          fixedUpmasterIds={activeUm ? [activeUm.id] : undefined}
                        />
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        );
      }
      case 'documents':
        return (
          <div className="page-scroll">
            <div className="page-view">
              <div className="page-header">
                <h2>📄 知识库 / 文档</h2>
                <p className="page-subtitle">浏览与对话文档</p>
              </div>
              <DocumentPanel />
            </div>
          </div>
        );
      case 'plans':
        return (
          <div className="page-scroll">
            <div className="page-view">
              <div className="page-header">
                <h2>📋 训练计划</h2>
                <p className="page-subtitle">管理你的训练计划</p>
              </div>
              <PlanView />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="app-shell">
        <Sidebar activeNav={activeNav} onNavigate={handleNavigate} health={health} />
        <div className="app-main flex-1 min-w-0 overflow-hidden">
          {renderContent()}
        </div>
      </div>
      <DataSourceRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
